import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { AkitaFeaturedQuery } from '@app/akita/api/featured/state/featured.query';
import { AkitaFeaturedService } from '@app/akita/api/featured/state/featured.service';
import { Product } from '@app/akita/api/products/models/product.model';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { ListOverlayOption } from '@app/overlays/list-overlay/models/list-overlay-config.interface';
import { ListOverlayDynamicService } from '@app/overlays/list-overlay/services/list-overlay-dynamic.service';
import { ApiError } from '@app/shared/models/api/api-error.model';
import { FeaturedReviewStatsModel } from '@app/shared/models/api/featured-review-stats.model';
import { FeaturedReviewModel } from '@app/shared/models/api/featured-review.model';
import { SentryUtil } from '@app/shared/utils/sentry.util';
import {
  BehaviorSubject,
  map,
  Observable,
  Subject,
  Subscription,
  takeUntil,
  tap,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
// Components
import { StarFilledIconComponent } from '@app/shared/components/icons/generated/star-filled/star-filled.component';
import { ArrowLeft1IconComponent } from '@app/shared/components/icons/generated/arrow-left-1/arrow-left-1.component';
import { TickOutlinedIconComponent } from '@app/shared/components/icons/generated/tick-outlined/tick-outlined.component';
import { ArrowDown1IconComponent } from '@app/shared/components/icons/generated/arrow-down-1/arrow-down-1.component';
import { AddIconComponent } from '@app/shared/components/icons/generated/add/add.component';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { StarHalfFilledIconComponent } from '@app/shared/components/icons/generated/star-half-filled/star-half-filled.component';
import { FeaturedReviewsItemComponent } from '../featured-reviews-item/featured-reviews-item.component';

const INITIAL_REVIEWS = 4;
const PAGE_INCREMENT = 10;

@Component({
  selector: 'app-featured-reviews-card',
  templateUrl: './featured-reviews-card.template.html',
  styleUrls: ['./featured-reviews-card.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    FeaturedReviewsItemComponent,
    TranslocoModule,
    // Icons
    StarFilledIconComponent,
    StarHalfFilledIconComponent,
    TickOutlinedIconComponent,
    ArrowLeft1IconComponent,
    ArrowDown1IconComponent,
    AddIconComponent,
    StarFilledIconComponent,
  ],
})
export class FeaturedReviewsCardComponent implements OnChanges, OnDestroy {
  @Input() public product?: Product | null;
  @Input() public showAll?: boolean | null = false;
  @Input() public collectionMode?: boolean | null = false;

  public readonly biggerThan$: Observable<ScreenSizeInfoState>;
  public reviewsLimit: number;
  public canShowMore: boolean;
  public reviewsTotalCount: number;
  public isAverageRatingPointFive = false;

  public phoneLanguage: string;
  public phoneModel: string;

  public featuredReviews$: Observable<Array<FeaturedReviewModel>>;
  public featuredReviewsStats$: Observable<FeaturedReviewStatsModel | null>;
  public fetchingFeaturedReviews$: Observable<boolean>;
  public featuredReviewsError$: Observable<ApiError | null>;

  public readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  private readonly selectedOptionSubject: BehaviorSubject<ListOverlayOption | null>;
  public selectedOption$: Observable<ListOverlayOption | null>;

  public sortingOptions: Array<ListOverlayOption> = [
    { display: 'label_top_ratings', value: 'top-ratings' },
    { display: 'label_bottom_ratings', value: 'bottom-ratings' },
  ];

  @ViewChild('sortPicker', { read: ElementRef })
  public sortPickerElem?: ElementRef | null;

  constructor(
    private readonly zone: NgZone,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaFeaturedQuery: AkitaFeaturedQuery,
    private readonly akitaFeaturedService: AkitaFeaturedService,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly translateService: TranslocoService,
    private readonly listOverlayDynamicService: ListOverlayDynamicService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();
    this.reviewsLimit = INITIAL_REVIEWS;
    this.reviewsTotalCount = 0;
    this.canShowMore = false;

    this.selectedOptionSubject = new BehaviorSubject(false as any);
    this.selectedOption$ = this.selectedOptionSubject.asObservable();
    this.selectedOptionSubject.next(this.sortingOptions[0]);

    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();

    this.phoneLanguage = `${
      this.akitaProductsQuery.validateLocale(this.translateService.getActiveLang())
        .language ||
      this.product?.language ||
      'en'
    }`;
    this.phoneModel = `${this.product?.model || ''}`;

    this.featuredReviews$ = this.akitaFeaturedQuery.selectFeaturedReviews(
      this.phoneLanguage,
      this.phoneModel
    );

    this.featuredReviewsStats$ = this.akitaFeaturedQuery.selectFeaturedReviewsStats(
      this.phoneLanguage,
      this.phoneModel
    );

    this.fetchingFeaturedReviews$ = this.akitaFeaturedQuery.selectFetchingFeaturedReviews(
      this.phoneLanguage
    );
    this.featuredReviewsError$ = this.akitaFeaturedQuery.selectFeaturedReviewsError(
      this.phoneLanguage
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      this.phoneLanguage = `${
        this.akitaProductsQuery.validateLocale(this.translateService.getActiveLang())
          .language ||
        this.product?.language ||
        'en'
      }`;
      this.phoneModel = this.product?.model || '';
      if (this.showAll) {
        this.fetchAllReviewsAsync();
      } else {
        this.fetchReviewsAsync();
      }
    }
  }

  private fetchReviewsAsync() {
    this.featuredReviews$ = this.akitaFeaturedQuery
      .selectFeaturedReviews(
        this.phoneLanguage,
        this.phoneModel,
        this.selectedOptionSubject.value?.value
      )
      .pipe(
        tap((result) => {
          this.reviewsTotalCount = result.length;
          if (result.length > INITIAL_REVIEWS) {
            this.canShowMore = true;
          }
        })
      );

    this.featuredReviewsStats$ = this.akitaFeaturedQuery
      .selectFeaturedReviewsStats(this.phoneLanguage, this.phoneModel)
      .pipe(
        tap((featuredReviewsStats: FeaturedReviewStatsModel | null) => {
          if (featuredReviewsStats?.averageRating) {
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            this.isAverageRatingPointFive = featuredReviewsStats.averageRating % 1 >= 0.4;
          }
        })
      );

    this.fetchingFeaturedReviews$ = this.akitaFeaturedQuery.selectFetchingFeaturedReviews(
      this.phoneLanguage
    );
    this.featuredReviewsError$ = this.akitaFeaturedQuery.selectFeaturedReviewsError(
      this.phoneLanguage
    );

    this.changeDetector.markForCheck();

    this.fetchReviewsInBG(this.phoneLanguage, this.phoneModel);
  }

  private fetchReviewsInBG(
    phoneLanguage?: string | null,
    phoneModel?: string | null
  ): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.zone.runOutsideAngular(() => {
        this.subscriptions.add(
          this.akitaFeaturedService
            .findFeaturedReviews(phoneLanguage, phoneModel)
            .pipe(takeUntil(this.destroySubject))
            .subscribe({
              next: () => {},
              error: () => {},
            })
        );
      });
    } else {
      this.subscriptions.add(
        this.akitaFeaturedService.findFeaturedReviewsAsync(phoneLanguage, phoneModel)
      );
    }
  }

  private fetchAllReviewsAsync() {
    this.featuredReviews$ = this.akitaFeaturedQuery
      .selectAllFeaturedReviews(this.phoneLanguage)
      .pipe(
        map((reviews: Array<FeaturedReviewModel>) => {
          const temp: Array<FeaturedReviewModel> = new Array(0);
          reviews.forEach((review) => {
            if (temp.findIndex((x) => x.model === review?.model) === -1) {
              temp.push(review);
            }
          });

          return temp;
        }),
        tap((result) => {
          this.reviewsTotalCount = result.length;
          if (result.length > INITIAL_REVIEWS) {
            this.canShowMore = true;
          }
        })
      );

    this.fetchingFeaturedReviews$ = this.akitaFeaturedQuery.selectFetchingFeaturedReviews(
      this.phoneLanguage
    );
    this.featuredReviewsError$ = this.akitaFeaturedQuery.selectFeaturedReviewsError(
      this.phoneLanguage
    );

    this.changeDetector.markForCheck();

    this.fetchAllReviewsInBG(this.phoneLanguage);
  }

  private fetchAllReviewsInBG(phoneLanguage?: string | null): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.zone.runOutsideAngular(() => {
        this.subscriptions.add(
          this.akitaFeaturedService.findAllReviewsAsync(phoneLanguage)
        );
        this.subscriptions.add(
          this.akitaFeaturedService.findAllReviewsAsync(phoneLanguage)
        );
      });
    } else {
      this.subscriptions.add(
        this.akitaFeaturedService.findAllReviewsAsync(phoneLanguage)
      );
      this.subscriptions.add(
        this.akitaFeaturedService.findAllReviewsAsync(phoneLanguage)
      );
    }
  }

  public trackByReview(index: number, value?: FeaturedReviewModel | null): string {
    return `${value?.title || index}`;
  }

  public getSearchUrlFragment(model: string): Array<string> | string {
    const fragments = this.akitaRouterQuery.searchUrlFragment;
    fragments.push(model);
    return fragments;
  }

  public showMore(): void {
    if (this.reviewsLimit < this.reviewsTotalCount) {
      this.reviewsLimit = this.reviewsLimit + PAGE_INCREMENT;
    }
    if (this.reviewsLimit >= this.reviewsTotalCount) {
      this.canShowMore = false;
    }
  }

  public toggleLanguagePicker(): boolean {
    this.subscriptions.add(
      this.listOverlayDynamicService.observeList
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (data?: any | null) => {
            const isOpen = Boolean(data !== null);

            if (
              !isOpen &&
              this.selectedOptionSubject.value !==
                this.listOverlayDynamicService.selectedOption
            ) {
              this.selectedOptionSubject.next(
                this.listOverlayDynamicService.selectedOption ||
                  this.selectedOptionSubject.value
              );
              if (this.showAll) {
                this.fetchAllReviewsAsync();
              } else {
                this.fetchReviewsAsync();
              }
            }
          },
        })
    );

    this.listOverlayDynamicService.open('list', {
      elementRef: this.sortPickerElem,
      data: {
        selectedOption: this.selectedOptionSubject.value,
        options: this.sortingOptions,
      },
      isBrowser: this.akitaRouterQuery.isBrowser,
    });

    SentryUtil.addBreadcrumb({
      category: 'ui',
      message: `Opened "Language Overlay" modal`,
      level: 'info',
      type: 'user',
    });

    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-member-accessibility
  getRatingPercentage(rating: number): number {
    const PER_CENT = 100;
    const percentage = Math.round((rating * PER_CENT) / this.reviewsTotalCount);
    return percentage;
  }

  public range(length: number | undefined): Array<number> {
    return Array(Math.floor(length || 0));
  }

  public trackByIndex(index: number): string {
    return `${index}`;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
